import React, { useState, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import GenericAutocomplete, {
  type GenericAutocompleteProps
} from '../FormAutoComplete'
import api from '../../../utils/api'
import { API_AUTOCOMPLETE_PAGE_SIZE } from '../../../consts'
import { stableSerializeRecord } from '../../../utils/stableSerializeRecord'
import { Box, CircularProgress } from '@mui/material'

interface ApiAutoCompleteProps<T extends Record<string, any>>
  extends Omit<
  GenericAutocompleteProps<T>,
  'getOptionLabel' | 'getOptionValue' | 'loading' | 'onChange' | 'options'
  > {
  selectedItem?: T
  apiEndpoint: string
  labelField: string
  valueField?: string
  WholeValue?: boolean
  onChange?: (value: any) => void
  getOptionLabel?: (option: T) => string
  getOptionSubLabel?: (option: T) => string
  apiParams?: Record<string, any>
  clearIcon?: React.ReactNode
  preloadCircle?: boolean
  skip?: boolean
  queryOptions?: Record<string, any>
}

export const ApiAutoComplete = <T extends Record<string, any>>({
  apiEndpoint,
  labelField,
  valueField: paramValueField,
  WholeValue,
  onChange,
  apiParams,
  clearIcon,
  skip = false,
  selectedItem,
  preloadCircle,
  getOptionLabel,
  getOptionSubLabel,
  ...props
}: ApiAutoCompleteProps<T>): JSX.Element => {
  const valueField = paramValueField ?? props.name
  const initialFetchDone = useRef(false)
  const [input, setInput] = useState<string>('')

  const reverseCamelCase = (str: string): string => str.replace(/([A-Z])/g, '_$1').toLowerCase()
  const labelFieldUnderscore = reverseCamelCase(labelField)

  const { data: rawOptions = [], isLoading, isFetching } = useQuery<T[]>({
    queryKey: ['search', apiEndpoint, input, stableSerializeRecord(apiParams as Record<string, unknown>), labelFieldUnderscore, skip],
    enabled: !skip,
    staleTime: skip ? 0 : 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await api.get(apiEndpoint, {
        params: {
          pageSize: API_AUTOCOMPLETE_PAGE_SIZE,
          search: input,
          sort: JSON.stringify({ field: labelFieldUnderscore, direction: 'asc' }),
          ...apiParams
        }
      })
      initialFetchDone.current = true
      return data
    },
    ...props.queryOptions
  })

  const mergedOptions = useMemo(() => {
    if (skip) {
      return selectedItem ? [selectedItem] : []
    }
    if (!selectedItem) return rawOptions

    const isItemPresent = rawOptions.some(
      item => item[valueField] === selectedItem[valueField]
    )
    return isItemPresent
      ? rawOptions
      : [selectedItem, ...rawOptions]
  }, [skip, rawOptions, selectedItem, valueField])

  const computedSelectedValue = selectedItem
    ? WholeValue
      ? selectedItem
      : selectedItem[valueField]
    : null

  if (!initialFetchDone.current && preloadCircle) {
    return (
      <Box justifyContent='center' display='flex'>
        <CircularProgress />
      </Box>
    )
  }
  return (
    <GenericAutocomplete
      {...props}
      options={mergedOptions}
      selectedValue={computedSelectedValue}
      getOptionLabel={option =>
        getOptionLabel
          ? getOptionLabel(option)
          : String((option as any)[labelField])
      }
      getOptionSubLabel={getOptionSubLabel}
      getOptionValue={(option: any) => option?.[valueField]}
      onInputChange={value => { setInput(value) }}
      loading={isLoading || isFetching}
      onChange={onChange}
      clearIcon={clearIcon}
    />
  )
}
